/**
 * Katanoid — Model Catalog & Tier Preferences
 *
 * Single source of truth for available models, tier definitions,
 * and user's per-tier model preferences (stored in localStorage).
 */

// ── Types ──

export type ModelTier = "free" | "lightweight" | "normal" | "expert";

export type ModelEntry = {
  id: string;           // key matching edge function MODELS map
  label: string;        // human-readable display name
  provider: string;     // "Anthropic" | "OpenAI" | "Google" | "DeepSeek"
  tier: ModelTier;      // default tier assignment
  creditCost: number;   // credits per message (0 = free)
};

export type TierInfo = {
  id: ModelTier;
  label: string;
  mult: string;         // display multiplier, e.g. "0×"
};

// ── Tier definitions ──

export const TIERS: TierInfo[] = [
  { id: "free",        label: "Free",   mult: "0×" },
  { id: "lightweight", label: "Lite",   mult: "1×" },
  { id: "normal",      label: "Normal", mult: "3×" },
  { id: "expert",      label: "Expert", mult: "15×" },
];

// ── Model catalog ──
// Order within each tier matters — first entry is the default.

export const MODEL_CATALOG: ModelEntry[] = [
  // Free tier
  {
    id: "google/gemini-flash",
    label: "Gemini Flash",
    provider: "Google",
    tier: "free",
    creditCost: 0,
  },

  // Lite tier
  {
    id: "anthropic/claude-haiku",
    label: "Claude Haiku",
    provider: "Anthropic",
    tier: "lightweight",
    creditCost: 1,
  },
  {
    id: "deepseek/deepseek-v3",
    label: "DeepSeek V3",
    provider: "DeepSeek",
    tier: "free",
    creditCost: 0,
  },

  // Normal tier
  {
    id: "anthropic/claude-sonnet",
    label: "Claude Sonnet 4",
    provider: "Anthropic",
    tier: "normal",
    creditCost: 3,
  },
  {
    id: "openai/gpt-4o",
    label: "GPT-4o",
    provider: "OpenAI",
    tier: "normal",
    creditCost: 2,
  },

  // Expert tier
  {
    id: "anthropic/claude-opus",
    label: "Claude Opus 4",
    provider: "Anthropic",
    tier: "expert",
    creditCost: 15,
  },
  {
    id: "openai/o1",
    label: "OpenAI o1",
    provider: "OpenAI",
    tier: "expert",
    creditCost: 12,
  },
];

// ── Custom model storage ──
// User-added models from the OpenRouter model browser (BYOK feature).
// Stored in memory; loaded from Supabase on auth.

let _customModels: ModelEntry[] = [];

/** Replace the in-memory custom model list (called after fetching from DB). */
export function setCustomModels(models: ModelEntry[]): void {
  _customModels = models;
}

/** Get the current in-memory custom models. */
export function getCustomModels(): ModelEntry[] {
  return _customModels;
}

/** Clear custom models (e.g. on sign-out). */
export function clearCustomModels(): void {
  _customModels = [];
}

// ── Helpers ──

/** Get all models available for a given tier (built-in + custom). */
export function getModelsForTier(tier: ModelTier): ModelEntry[] {
  const builtIn = MODEL_CATALOG.filter((m) => m.tier === tier);
  const custom = _customModels.filter((m) => m.tier === tier);
  return [...builtIn, ...custom];
}

/** Get the default model ID for a tier (first in catalog) */
export function getDefaultModelForTier(tier: ModelTier): string {
  const models = getModelsForTier(tier);
  return models.length > 0 ? models[0].id : MODEL_CATALOG[0].id;
}

/** Look up a model entry by ID (checks built-in then custom). */
export function getModelById(id: string): ModelEntry | undefined {
  return MODEL_CATALOG.find((m) => m.id === id) ?? _customModels.find((m) => m.id === id);
}

// ── Vision model options ──
// Models available for the image_analyze tool. Credit cost is per tool call.

export type VisionModelEntry = {
  id: string;
  label: string;
  creditCost: number;
};

export const VISION_MODELS: VisionModelEntry[] = [
  { id: "anthropic/claude-sonnet-4", label: "Claude Sonnet 4", creditCost: 2 },
  { id: "openai/gpt-4o", label: "GPT-4o", creditCost: 2 },
  { id: "anthropic/claude-haiku-4.5", label: "Claude Haiku 4.5", creditCost: 1 },
  { id: "google/gemini-2.5-flash", label: "Gemini Flash", creditCost: 0 },
];

export const DEFAULT_VISION_MODEL = "anthropic/claude-sonnet-4";

// ── TTS voice options ──

export type TtsVoiceEntry = {
  id: string;
  label: string;
  description: string;
};

export const TTS_VOICES: TtsVoiceEntry[] = [
  { id: "alloy", label: "Alloy", description: "Neutral, balanced" },
  { id: "nova", label: "Nova", description: "Warm, female" },
  { id: "shimmer", label: "Shimmer", description: "Soft, female" },
  { id: "echo", label: "Echo", description: "Clear, male" },
  { id: "onyx", label: "Onyx", description: "Deep, male" },
  { id: "fable", label: "Fable", description: "Expressive, British" },
];

export const DEFAULT_TTS_VOICE = "alloy";

/** TTS credit cost is fixed regardless of voice */
export const TTS_CREDIT_COST = 1;

// ── Tool preferences (localStorage) ──

const TOOL_PREFS_KEY = "katanoid_tool_prefs";

export type VoiceMode = "hold" | "toggle";

export type ToolPreferences = {
  visionModel: string;
  ttsVoice: string;
  voiceMode: VoiceMode;
};

function getDefaultToolPreferences(): ToolPreferences {
  return {
    visionModel: DEFAULT_VISION_MODEL,
    ttsVoice: DEFAULT_TTS_VOICE,
    voiceMode: "toggle",
  };
}

export function loadToolPreferences(): ToolPreferences {
  const defaults = getDefaultToolPreferences();
  try {
    const raw = localStorage.getItem(TOOL_PREFS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<ToolPreferences>;
    return {
      visionModel:
        parsed.visionModel && VISION_MODELS.find((m) => m.id === parsed.visionModel)
          ? parsed.visionModel
          : defaults.visionModel,
      ttsVoice:
        parsed.ttsVoice && TTS_VOICES.find((v) => v.id === parsed.ttsVoice)
          ? parsed.ttsVoice
          : defaults.ttsVoice,
      voiceMode:
        parsed.voiceMode === "hold" || parsed.voiceMode === "toggle"
          ? parsed.voiceMode
          : defaults.voiceMode,
    };
  } catch {
    return defaults;
  }
}

export function saveToolPreferences(prefs: ToolPreferences): void {
  try {
    localStorage.setItem(TOOL_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable
  }
}

// ── Tier preferences (localStorage) ──

const PREFS_KEY = "katanoid_model_prefs";

export type TierPreferences = Record<ModelTier, string>; // tier → model ID

function getDefaultPreferences(): TierPreferences {
  return {
    free: getDefaultModelForTier("free"),
    lightweight: getDefaultModelForTier("lightweight"),
    normal: getDefaultModelForTier("normal"),
    expert: getDefaultModelForTier("expert"),
  };
}

/** Load user's per-tier model preferences from localStorage */
export function loadTierPreferences(): TierPreferences {
  const defaults = getDefaultPreferences();
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<TierPreferences>;
    return {
      free: parsed.free && getModelById(parsed.free) ? parsed.free : defaults.free,
      lightweight: parsed.lightweight && getModelById(parsed.lightweight) ? parsed.lightweight : defaults.lightweight,
      normal: parsed.normal && getModelById(parsed.normal) ? parsed.normal : defaults.normal,
      expert: parsed.expert && getModelById(parsed.expert) ? parsed.expert : defaults.expert,
    };
  } catch {
    return defaults;
  }
}

/** Save user's per-tier model preferences to localStorage */
export function saveTierPreferences(prefs: TierPreferences): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable
  }
}

/** Get the user's configured model ID for a specific tier */
export function getModelForTier(tier: ModelTier): string {
  const prefs = loadTierPreferences();
  return prefs[tier];
}
